import type { TimelineEntry } from "@/lib/activityTimeline/types";
import type { TimelineDisplayScope } from "@/lib/activityTimeline/tier2Redaction";
import { ActivityTimelineRow, type ActivityTimelineRowVariant } from "./ActivityTimelineRow";

type Props = {
  entries: TimelineEntry[];
  total: number;
  loading: boolean;
  error: string | null;
  scope: TimelineDisplayScope;
  variant?: ActivityTimelineRowVariant;
  hasMore?: boolean;
  working?: boolean;
  onLoadMore?: () => void;
  page?: number;
  pageCount?: number;
  pageSize?: number;
  pageSizeOptions?: number[];
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  onDrillEntity?: (entityId: string, entityType: string) => void;
  drillEntityId?: string | null;
  onClearDrill?: () => void;
  emptyMessage?: string;
};

export function ActivityTimelineList({
  entries,
  total,
  loading,
  error,
  scope,
  variant = "default",
  hasMore = false,
  working = false,
  onLoadMore,
  page,
  pageCount,
  pageSize,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
  onDrillEntity,
  drillEntityId = null,
  onClearDrill,
  emptyMessage = "No activity matches these filters.",
}: Props) {
  if (loading) {
    return <div className="text-sm text-gray-500 dark:text-zinc-400">Loading activity…</div>;
  }
  if (error) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-900/25 dark:text-amber-100">
        {error}
      </div>
    );
  }
  if (entries.length === 0) {
    return (
      <div className="space-y-3">
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500 dark:border-zinc-600 dark:bg-zinc-900/40 dark:text-zinc-400">
          {emptyMessage}
        </div>
        {onPageChange && pageSizeOptions && onPageSizeChange ? (
          <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-zinc-300">
            Per page
            <select
              className="rounded border border-gray-300 bg-white px-2 py-1 text-sm dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>
    );
  }

  return (
    <>
      {drillEntityId && onClearDrill ? (
        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-900 dark:border-indigo-900 dark:bg-indigo-950/30 dark:text-indigo-100">
          <span>
            Entity drill: <span className="font-mono">{drillEntityId}</span>
          </span>
          <button
            type="button"
            className="rounded border border-indigo-300 bg-white px-2 py-0.5 text-xs font-medium text-indigo-800 hover:bg-indigo-100 dark:border-indigo-700 dark:bg-zinc-900 dark:text-indigo-200"
            onClick={onClearDrill}
          >
            Clear drill
          </button>
        </div>
      ) : null}
      <div className="text-xs text-gray-500 dark:text-zinc-400">
        {onPageChange && pageSize != null && page != null
          ? `Showing ${page * pageSize + 1}–${page * pageSize + entries.length} of ${total}`
          : `Showing ${entries.length} of ${total}`}
      </div>
      <ul className="mt-2 divide-y divide-gray-100 dark:divide-zinc-700">
        {entries.map((entry) => (
          <ActivityTimelineRow
            key={entry.id}
            entry={entry}
            scope={scope}
            variant={variant}
            onDrillEntity={onDrillEntity}
            drillActive={Boolean(drillEntityId && entry.entity_id === drillEntityId)}
          />
        ))}
      </ul>
      {onPageChange && page != null ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {pageSizeOptions && pageSizeOptions.length > 0 && onPageSizeChange ? (
            <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-zinc-300">
              Per page
              <select
                className="rounded border border-gray-300 bg-white px-2 py-1 text-sm dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100"
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
              >
                {pageSizeOptions.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <button
            type="button"
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 disabled:opacity-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100"
            onClick={() => onPageChange(Math.max(0, page - 1))}
            disabled={working || page <= 0}
          >
            Previous
          </button>
          <span className="text-xs text-gray-600 dark:text-zinc-300">
            Page {page + 1}
            {pageCount ? ` of ${pageCount}` : ""}
          </span>
          <button
            type="button"
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 disabled:opacity-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100"
            onClick={() => onPageChange(page + 1)}
            disabled={working || !pageCount || page + 1 >= pageCount}
          >
            Next
          </button>
        </div>
      ) : hasMore && onLoadMore ? (
        <div className="mt-3">
          <button
            type="button"
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 disabled:opacity-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100"
            onClick={() => void onLoadMore()}
            disabled={working}
          >
            {working ? "Loading…" : "Load more"}
          </button>
        </div>
      ) : null}
    </>
  );
}
